import React from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import Upstage from './upstage'

const D = console.log

const container = document.getElementById('root')

if (!container) {
    throw Error('Root element not found')
}

interface IErrorBoundProps {
  children: React.ReactNode
  fallback?: React.ReactNode
}

interface IErrorBoundState {
  error: boolean
}

// React component example

/**
 * INTERCEPT unhandled exceptions (i.e. white-screen of death)
 */
class ErrorBound extends React.Component<IErrorBoundProps, IErrorBoundState> {
    // PREVIOUS global error handler
    private prevOnError: OnErrorEventHandler | null = null

    constructor (props) {
        super(props)
        this.state = { error: false }
    }

    componentDidMount () {
        // INJECT OUR OWN GLOBAL ERROR HANDLER
        this.prevOnError = window.onerror
        window.onerror = (message, source, lineno, colno, error) => {
        D('ErrorBound: window.onerror:', error)
        this.setState({ error: true })
        
        // INVOKE previous handler, if exists
        if (this.prevOnError) {
            return this.prevOnError(message, source, lineno, colno, error)
        }
        return true
        }
    }

    componentWillUnmount () {
        // RESTORE previous global error handler
        window.onerror = this.prevOnError
    }

    static getDerivedStateFromError (error) {
        return { error: true }
    }

    render () {
        if (this.state.error && this.props.fallback) {
            return this.props.fallback
        } else {
            return this.props.children
        }
    }
}

const BadBrowser = ({mobile}:{mobile:boolean}) => {
    return (
        <div style={{
            width: '100%',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: 0,
            padding: 0,
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: 'black'
        }}>
            <div style={{
                position: 'relative',
                width: '90vmin',
                height: '90vmin',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <svg 
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '150%',
                        height: '150%',
                        opacity: '25%',
                    }}
                    viewBox="0 0 70 50"
                    preserveAspectRatio="xMidYMid meet"
                >
                    <g>
                        <path fill="red" d="M5.72927 4.76169C5.24456 4.75891 4.89132 5.15082 4.88855 5.63692L4.87906 26.3893C4.86712 27.0179 5.56669 27.4493 6.12838 27.1424L22.7651 18.8227C23.3268 18.5158 24.0264 18.9473 24.0145 19.5758L24.0174 26.3983C24.0055 27.0269 24.705 27.4583 25.2667 27.1514L46.5299 16.5282C46.7984 16.3453 47.0174 16.0448 46.994 15.743L46.9985 6.14625C47.0104 5.51771 46.3109 5.08625 45.7492 5.39315L29.1124 13.7129C28.509 14.0027 27.8512 13.5883 27.8631 12.9597L27.8601 6.13724C27.8721 5.5087 27.1725 5.07724 26.6108 5.38414L9.97406 13.7038C9.37068 13.9937 8.71281 13.5793 8.68305 12.9337L8.68287 5.62512C8.68564 5.13902 8.31181 4.74295 7.8271 4.74017L5.72927 4.76169Z"/>
                    </g>
                </svg>
                <div style={{
                    color: 'white',
                    fontFamily: 'Arial, sans-serif',
                    opacity: '50%',
                    fontSize: '2.2rem',
                }}>
                    {mobile 
                        ? 'ProPatch Web App cannot operate on a mobile device'
                        : 'ProPatch Web App requires a modern Chrome-compatible browser'
                    }
                </div>
            </div>
      </div>
    )
  }

const App = () => {
    // SAFARI
    function isSafari () : boolean {
        // PRIMARY: Safari object exists
        if (typeof window === 'undefined' || !('safari' in window) || !window.safari) {
            return false;
        }
        // SECONDARY: UA contains Safari but no other browsers
        const ua: string = navigator.userAgent
        return /Safari/.test(ua) && !/Chrome|Chromium|Edge|Firefox|Opera/.test(ua)
    }

    function isMobileDevice (): boolean {
        const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
        const small = window.innerWidth < 768
        const mobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        return hasTouch && (small || mobileUA)
    }
    
    const [incompat, setIncompat] = React.useState<boolean>(false)
    const [mobile, setMobile] = React.useState<boolean>(false)
    React.useEffect(() => {
        if (isMobileDevice()) {
            setMobile(true)
        }
        else {
            // DETECT browser is Chrome/Safari
            const ua = navigator.userAgent.toLowerCase()
            const chrome = ua.includes('chrome') && !!window['chrome']
            setIncompat(!chrome && !isSafari())
        }
    }, [])

    return (
       (incompat || mobile)
        ? <BadBrowser mobile={mobile}/>
        : <ErrorBound fallback={<Upstage fallback={true}/>}>
            <Upstage/>
        </ErrorBound>
    )
 }

const root = createRoot(container)

root.render(<App/>)
